import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isTransparentBg = transparent && !isScrolled;

  const navLinks = [
    { label: '首页', href: '/' },
    { label: '分类', href: '/category/all' },
    { label: '排行榜', href: '/category/all?sort=rating' },
    { label: '最新上架', href: '/category/all?sort=newest' },
    { label: '关于我们', href: '/about' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparentBg
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-sm border-b border-background-200/70'
      }`}
    >
      <div className="mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-book-read-line text-lg text-white"></i>
            </div>
            <span className={`text-lg font-bold font-heading tracking-tight whitespace-nowrap ${
              isTransparentBg ? 'text-white' : 'text-foreground-900'
            }`}>
              36漫画
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = link.href === '/' 
                ? location.pathname === '/' 
                : location.pathname.startsWith(link.href.split('?')[0]);
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    isActive
                      ? isTransparentBg
                        ? 'bg-white/20 text-white'
                        : 'bg-primary-50 text-primary-700'
                      : isTransparentBg
                        ? 'text-white/80 hover:text-white hover:bg-white/10'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="搜索漫画..."
                className="pl-9 pr-4 py-2 text-sm rounded-full bg-background-100 border border-background-200/70 text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent w-48 transition-all duration-200"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            </div>
            <Link
              to="/about"
              className="px-5 py-2 text-sm font-medium bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              开始阅读
            </Link>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="菜单"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => {
              const isActive = link.href === '/' 
                ? location.pathname === '/' 
                : location.pathname.startsWith(link.href.split('?')[0]);
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap ${
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
            <div className="pt-3 px-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="搜索漫画..."
                  className="pl-9 pr-4 py-2.5 text-sm rounded-full w-full bg-background-100 border border-background-200/70 text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                />
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              </div>
            </div>
            <Link
              to="/about"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block mx-4 mt-3 px-5 py-3 text-sm font-medium bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors duration-200 text-center whitespace-nowrap cursor-pointer"
            >
              开始阅读
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}